// Data hooks for the meetings pages.
//
// - `useMeetingsStatus`: `GET /api/meetings/status`. `null` while it loads.
//   Any failure reads as `{enabled:false}`: the Focus "Meetings" button stays
//   hidden and the routes show "not found", the same as an environment with
//   the feature off.
// - `useMeetingList` / `useMeeting`: one project's list (none without a
//   project) and one meeting. `useUnassignedMeetings`: the meetings with no
//   project yet, read once. They poll every
//   `MEETING_POLL_MS` ONLY while something on screen still moves on its own
//   (a meeting whose status is not settled, an issue row still being filed);
//   once everything has settled they stop. Waits are server facts, never a
//   browser clock (`apps/web/CLAUDE.md`).

import { useCallback, useEffect, useRef, useState } from "react";

import type {
  MeetingDetailDto,
  MeetingIssueRowDto,
  MeetingListItemDto,
  MeetingsStatusDto,
} from "@loom/shared/meetings";

import { HttpError } from "../../lib/http";

import { listMeetings, readMeeting, readMeetingsStatus } from "./meetingApi";
import { isSettledMeeting } from "./meetingCopy";

/** How often a page with something still in motion reads again. */
export const MEETING_POLL_MS = 30_000;

const DISABLED: MeetingsStatusDto = { enabled: false };

export const useMeetingsStatus = (): {
  readonly status: MeetingsStatusDto | null;
  readonly reload: () => void;
} => {
  const [status, setStatus] = useState<MeetingsStatusDto | null>(null);
  const [bump, setBump] = useState(0);
  useEffect(() => {
    let alive = true;
    readMeetingsStatus()
      .then((next) => {
        if (alive) setStatus(next);
      })
      .catch(() => {
        if (alive) setStatus(DISABLED);
      });
    return () => {
      alive = false;
    };
  }, [bump]);
  const reload = useCallback(() => setBump((n) => n + 1), []);
  return { status, reload };
};

/** A row still being filed moves on its own; every other state waits on someone. */
const rowInMotion = (row: MeetingIssueRowDto): boolean =>
  row.state === "drafted" || row.state === "filing";

export const meetingInMotion = (m: MeetingListItemDto): boolean => !isSettledMeeting(m);

const detailInMotion = (m: MeetingDetailDto): boolean =>
  meetingInMotion(m) || m.issues.some(rowInMotion);

/** Re-run `tick` every `MEETING_POLL_MS` while `active`; stop when it is not. */
const usePoll = (active: boolean, tick: () => void): void => {
  const tickRef = useRef(tick);
  tickRef.current = tick;
  useEffect(() => {
    if (!active) return undefined;
    const id = window.setInterval(() => tickRef.current(), MEETING_POLL_MS);
    return () => window.clearInterval(id);
  }, [active]);
};

export interface MeetingListState {
  readonly meetings: readonly MeetingListItemDto[] | null;
  readonly failed: boolean;
  readonly hasMore: boolean;
  readonly loadingMore: boolean;
  readonly loadMore: () => void;
  readonly reload: () => void;
}

export const useMeetingList = (enabled: boolean, projectId: number | null): MeetingListState => {
  const [meetings, setMeetings] = useState<MeetingListItemDto[] | null>(null);
  const [nextBefore, setNextBefore] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  // A poll re-reads only the first page, so only its rows decide whether to
  // poll: a moving row from an older page would keep the poll running while
  // it never updates.
  const [firstPageInMotion, setFirstPageInMotion] = useState(false);
  const generation = useRef(0);
  // How many older pages the user opened: a poll re-reads the first page and
  // keeps the older ones it already has.
  const olderRef = useRef<MeetingListItemDto[]>([]);

  // The route remounts the page per project (`key`), so a project's rows
  // never meet another's here.
  const reload = useCallback(() => {
    if (!enabled || projectId === null) return;
    const mine = ++generation.current;
    listMeetings(projectId)
      .then((page) => {
        if (mine !== generation.current) return;
        const seen = new Set(page.meetings.map((m) => m.meetingId));
        const older = olderRef.current.filter((m) => !seen.has(m.meetingId));
        setMeetings([...page.meetings, ...older]);
        setFirstPageInMotion(page.meetings.some(meetingInMotion));
        if (older.length === 0) setNextBefore(page.nextBefore);
        setFailed(false);
      })
      .catch(() => {
        if (mine === generation.current) setFailed(true);
      });
  }, [enabled, projectId]);

  useEffect(() => {
    reload();
    return () => {
      generation.current += 1;
    };
  }, [reload]);

  const loadMore = useCallback(() => {
    if (nextBefore === null || loadingMore || projectId === null) return;
    setLoadingMore(true);
    listMeetings(projectId, nextBefore)
      .then((page) => {
        olderRef.current = [...olderRef.current, ...page.meetings];
        setMeetings((prev) => {
          const have = new Set((prev ?? []).map((m) => m.meetingId));
          return [...(prev ?? []), ...page.meetings.filter((m) => !have.has(m.meetingId))];
        });
        setNextBefore(page.nextBefore);
      })
      .catch(() => setFailed(true))
      .finally(() => setLoadingMore(false));
  }, [nextBefore, loadingMore, projectId]);

  usePoll(enabled && projectId !== null && firstPageInMotion, reload);

  return {
    meetings,
    failed,
    hasMore: nextBefore !== null,
    loadingMore,
    loadMore,
    reload,
  };
};

/**
 * The owner's meetings with no project yet (a Meet link reused from before
 * every launch named one): parked until the owner picks a project on the
 * meeting's page. One read of the newest page, no polling; an empty list or
 * a failed read shows nothing.
 */
export const useUnassignedMeetings = (enabled: boolean): readonly MeetingListItemDto[] => {
  const [meetings, setMeetings] = useState<readonly MeetingListItemDto[]>([]);
  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    listMeetings("none")
      .then((page) => {
        if (alive) setMeetings(page.meetings);
      })
      .catch(() => {
        // Nothing to show: the project's own list is unaffected.
      });
    return () => {
      alive = false;
    };
  }, [enabled]);
  return meetings;
};

export type MeetingLoad =
  | { readonly kind: "loading" }
  | { readonly kind: "not_found" }
  | { readonly kind: "failed" }
  | { readonly kind: "ready"; readonly meeting: MeetingDetailDto };

export interface MeetingState {
  readonly load: MeetingLoad;
  readonly reload: () => void;
  /** Put one row the server just answered in place of the old one. */
  readonly replaceIssue: (row: MeetingIssueRowDto) => void;
}

export const useMeeting = (meetingId: string, enabled: boolean): MeetingState => {
  const [load, setLoad] = useState<MeetingLoad>({ kind: "loading" });
  const generation = useRef(0);

  const reload = useCallback(() => {
    if (!enabled) return;
    const mine = ++generation.current;
    readMeeting(meetingId)
      .then((meeting) => {
        if (mine === generation.current) setLoad({ kind: "ready", meeting });
      })
      .catch((err: unknown) => {
        if (mine !== generation.current) return;
        setLoad(
          err instanceof HttpError && err.status === 404
            ? { kind: "not_found" }
            : { kind: "failed" },
        );
      });
  }, [meetingId, enabled]);

  useEffect(() => {
    setLoad({ kind: "loading" });
    reload();
    return () => {
      generation.current += 1;
    };
  }, [reload]);

  const replaceIssue = useCallback((row: MeetingIssueRowDto) => {
    setLoad((prev) =>
      prev.kind !== "ready"
        ? prev
        : {
            kind: "ready",
            meeting: {
              ...prev.meeting,
              issues: prev.meeting.issues.some((i) => i.ticketId === row.ticketId)
                ? prev.meeting.issues.map((i) => (i.ticketId === row.ticketId ? row : i))
                : [...prev.meeting.issues, row],
            },
          },
    );
  }, []);

  usePoll(enabled && load.kind === "ready" && detailInMotion(load.meeting), reload);

  return { load, reload, replaceIssue };
};
